/**
 * Leaderboard Service
 *
 * Calculates group rankings and contributor performance
 * - Message count per user
 * - Accuracy tracking (sentiment vs actual market movement)
 * - Streak tracking (consecutive accurate predictions)
 * - Badge system (🎯 accurate, ⭐ contrarian)
 *
 * This enables "Beat the Market" gamification
 */

import { supabase } from '@/services/supabase/supabaseClient';

export interface LeaderboardUser {
  userId: string;
  userName: string;
  userAvatar: string | null;
  messageCount: number;
  accuracy: number; // 0-100 (if we track predictions)
  streak: number; // Consecutive days with messages
  isAccurate: boolean; // Badge: 🎯 (accuracy > 70%)
  isContrarian: boolean; // Badge: ⭐ (against market trend)
  lastMessageAt: string;
}

export interface GroupRanking {
  groupId: string;
  groupName: string;
  rank: number; // Global rank among all groups
  accuracy: number; // Group average accuracy vs market
  temperature: number; // 0-3 (🔥 count - activity level)
  memberCount: number;
  messageCount: number;
}

class LeaderboardService {
  /**
   * Get top contributors in a group (for dense leaderboard)
   */
  async getTopContributors(
    groupId: string,
    limit: number = 3
  ): Promise<{ data: LeaderboardUser[] | null; error: string | null }> {
    try {
      console.log('[Leaderboard] Fetching top contributors for group:', groupId);

      // Fetch all messages in the group
      const { data: messages, error: messagesError } = await supabase
        .from('messages')
        .select('id, user_id, created_at, metadata')
        .eq('group_id', groupId)
        .order('created_at', { ascending: false });

      if (messagesError) {
        console.error('[Leaderboard] Error fetching messages:', messagesError);
        return { data: null, error: 'Failed to fetch messages' };
      }

      if (!messages || messages.length === 0) {
        return { data: [], error: null };
      }

      // Aggregate by user
      const userStats = new Map<string, { count: number; lastMessageAt: string }>();

      messages.forEach((msg: { user_id: string; created_at: string }) => {
        const existing = userStats.get(msg.user_id);
        if (existing) {
          existing.count++;
        } else {
          userStats.set(msg.user_id, {
            count: 1,
            lastMessageAt: msg.created_at,
          });
        }
      });

      // Fetch user profiles
      const userIds = Array.from(userStats.keys());
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, name, avatar_url')
        .in('id', userIds);

      if (profilesError) {
        console.error('[Leaderboard] Error fetching profiles:', profilesError);
        return { data: null, error: 'Failed to fetch profiles' };
      }

      // Build leaderboard
      const leaderboard: LeaderboardUser[] = (profiles || [])
        .map((profile: { id: string; name: string | null; avatar_url: string | null }) => {
          const stats = userStats.get(profile.id);
          if (!stats) return null;

          // Calculate streak (simplified: days with messages in last 30 days)
          const streak = this.calculateStreak(
            messages.filter(
              (m: { user_id: string; created_at: string }) => m.user_id === profile.id
            )
          );

          // Calculate accuracy (placeholder - requires prediction tracking)
          const accuracy = 0; // TODO: Implement when we track predictions

          // Badges
          const isAccurate = accuracy >= 70;
          const isContrarian = false; // TODO: Compare user sentiment vs market

          return {
            userId: profile.id,
            userName: profile.name || 'Anonymous',
            userAvatar: profile.avatar_url,
            messageCount: stats.count,
            accuracy,
            streak,
            isAccurate,
            isContrarian,
            lastMessageAt: stats.lastMessageAt,
          };
        })
        .filter((u): u is LeaderboardUser => u !== null)
        .sort((a, b) => b.messageCount - a.messageCount) // Sort by message count
        .slice(0, limit);

      console.log('[Leaderboard] Top contributors:', leaderboard.length);
      return { data: leaderboard, error: null };
    } catch (error) {
      console.error('[Leaderboard] Error getting top contributors:', error);
      return { data: null, error: 'Failed to get top contributors' };
    }
  }

  /**
   * Calculate user streak (consecutive days with messages)
   */
  private calculateStreak(userMessages: { created_at: string }[]): number {
    if (userMessages.length === 0) return 0;

    // Get unique dates (YYYY-MM-DD)
    const dates = userMessages
      .map(m => new Date(m.created_at).toISOString().split('T')[0])
      .filter((date, index, self) => self.indexOf(date) === index) // Unique
      .sort()
      .reverse(); // Most recent first

    // Count consecutive days from today backwards
    let streak = 0;
    const today = new Date().toISOString().split('T')[0];
    let currentDate = new Date(today);

    for (const messageDate of dates) {
      const checkDate = currentDate.toISOString().split('T')[0];

      if (messageDate === checkDate) {
        streak++;
        currentDate.setDate(currentDate.getDate() - 1); // Go back one day
      } else {
        // Gap found, stop counting
        break;
      }
    }

    return streak;
  }

  /**
   * Get group ranking (global position among all groups)
   */
  async getGroupRanking(
    groupId: string
  ): Promise<{ data: GroupRanking | null; error: string | null }> {
    try {
      console.log('[Leaderboard] Fetching group ranking for:', groupId);

      // Fetch all groups with message counts
      const { data: groups, error: groupsError } = await supabase.from('groups').select('id, name'); // Fetch all groups

      if (groupsError) {
        console.error('[Leaderboard] Error fetching groups:', groupsError);
        return { data: null, error: 'Failed to fetch groups' };
      }

      if (!groups || groups.length === 0) {
        return { data: null, error: 'No groups found' };
      }

      // Calculate message counts for each group (in parallel)
      const groupStats = await Promise.all(
        groups.map(async (group: { id: string; name: string }) => {
          const { count } = await supabase
            .from('messages')
            .select('id', { count: 'exact', head: true })
            .eq('group_id', group.id);

          return {
            groupId: group.id,
            groupName: group.name,
            messageCount: count || 0,
          };
        })
      );

      // Sort by message count and assign ranks
      const rankedGroups = groupStats
        .sort((a, b) => b.messageCount - a.messageCount)
        .map((group, index) => ({
          ...group,
          rank: index + 1,
          accuracy: 0, // TODO: Calculate vs market
          temperature: this.calculateTemperature(group.messageCount),
          memberCount: 0, // TODO: Fetch member count from group_members
        }));

      // Find current group
      const currentGroup = rankedGroups.find(g => g.groupId === groupId);

      if (!currentGroup) {
        return { data: null, error: 'Group not found in rankings' };
      }

      console.log('[Leaderboard] Group rank:', currentGroup.rank);
      return { data: currentGroup as GroupRanking, error: null };
    } catch (error) {
      console.error('[Leaderboard] Error getting group ranking:', error);
      return { data: null, error: 'Failed to get group ranking' };
    }
  }

  /**
   * Calculate temperature (activity level) - 0 to 3 (🔥 count)
   * Based on messages in last 24h
   */
  private calculateTemperature(messageCount: number): number {
    if (messageCount > 100) return 3; // 🔥🔥🔥 Very active
    if (messageCount > 50) return 2; // 🔥🔥 Active
    if (messageCount > 10) return 1; // 🔥 Moderate
    return 0; // Cold
  }

  /**
   * Calculate group accuracy vs market (placeholder for future implementation)
   */
  async calculateGroupAccuracy(groupId: string): Promise<number> {
    // TODO: Implement when we track predictions
    // 1. Get all predictions (bullish/bearish sentiment) from group
    // 2. Compare with actual market movement
    // 3. Return accuracy percentage
    return 0;
  }
}

export const leaderboardService = new LeaderboardService();
